import { useClientPortalAPI, useFormikSubmit, APIContext } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  Card,
  Divider,
  LinkButton,
  PrimaryButton,
  Radio,
  RadioGroupFormikField,
  SelectFormikField,
  SelectItem,
  Stack,
  Text,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { TextField } from "@certn/cascadia-react/components/inputs/text-field/TextField";
import { css } from "@emotion/css";
import { Form, Formik } from "formik";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useUser } from "../../../hooks/useUser";
import { Schemas } from "../../../types";
import { AtsIntegrationHeader } from "../AtsIntegrationHeader";
import { ApiKeyWhatsThis } from "./components/ApiKeyWhatsThis";
import { DefaultPackageWhatsThis } from "./components/DefaultPackageWhatsThis";
import { WebhookSignatureWhatsThis } from "./components/WebhookSignatureWhatsThis";

interface Props {
  initialValues: components["schemas"]["AtsIntegrationList"];
  reload: () => void;
}

export function LeverIntegration({ initialValues, reload }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { details: user } = useUser();
  const { domain } = useContext(APIContext);

  const { data: packages, isLoading: isLoadingPackages } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/packages/"
  );

  const { data: customerGroups, isLoading: isLoadingCustomerGroups } =
    useClientPortalAPI(
      "/api/internal/client-portal/ats-integrations/customer-groups/"
    );

  const { data: triggerStages, isLoading: isLoadingTriggerStages } =
    useClientPortalAPI(
      `/api/internal/client-portal/ats-integrations/${initialValues.id}/trigger-stages/`
    );

  const { submit, isSubmitting } = useFormikSubmit<
    Schemas["PublicAPIKeyCreateResponse"]
  >(
    `/api/internal/client-portal/ats-integrations/${initialValues.id}/update/`,
    {
      onSuccess: () => {
        showToast(t("Integration updated successfully"), {
          type: "success",
        });
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    }
  );

  const webhookUrl = `${domain}/api/external/ats/lever/${user?.client_account?.id}/check/create/`;
  const copyToClipBoard = () => {
    navigator.clipboard.writeText(webhookUrl);
    showToast(t("URL copied to clipboard"), { type: "success" });
  };

  return (
    <>
      <Stack gap="space-2" className={styles.stack}>
        <Text style="global/heading-large">{t("Integration details")}</Text>
        <Formik initialValues={initialValues} onSubmit={submit}>
          <Form>
            <AtsIntegrationHeader
              integrationName="Lever"
              initialValues={initialValues}
              reload={reload}
            />
            <Card type="large" className={styles.card}>
              <Stack gap="space-4">
                <Stack gap="space-4">
                  <Text style="global/heading-medium">
                    {t("Details to add to your {{integration}} app", {
                      integration: "Lever",
                    })}
                  </Text>
                  <Stack orientation="horizontal" align="end" gap="space-2">
                    <TextField
                      className={styles.textFieldSpacing}
                      testID="webhook-url"
                      label={t("Webhook URL")}
                      value={webhookUrl}
                      isDisabled
                      isRequired
                    />
                    <BasicButton
                      onPress={copyToClipBoard}
                      type="button"
                      size="medium"
                      testID="copy-link-button"
                      leftIcon="link"
                    >
                      {t("Copy link")}
                    </BasicButton>
                  </Stack>
                </Stack>
                <Divider marginTop="space-4" />
                <TextInputFormikField
                  label={t("API key")}
                  testID="api-key"
                  name="lever_integration.api_key"
                  info={<ApiKeyWhatsThis />}
                  isDisabled
                  isRequired
                />
                <TextInputFormikField
                  label={t("Webhook signature token")}
                  testID="webhook-signature"
                  name="lever_integration.signature_token"
                  isRequired
                  isDisabled={!initialValues.is_active}
                  info={<WebhookSignatureWhatsThis />}
                />
                <RadioGroupFormikField
                  label={t("Report URL type")}
                  testID="report-url-type"
                  name="report_url_type"
                  orientation="horizontal"
                  isRequired
                  isDisabled={!initialValues.is_active}
                  defaultValue="DIRECT_PDF_URL"
                >
                  <Radio testID="direct-pdf-url" value="DIRECT_PDF_URL">
                    {t("PDF URL")}
                  </Radio>
                  <Radio testID="client-portal-url" value="CLIENT_PORTAL_URL">
                    {t("Client Portal URL (Authenticated)")}
                  </Radio>
                </RadioGroupFormikField>
                <Divider />
                <section>
                  <SelectFormikField
                    name="lever_integration.trigger_stage_id"
                    label={t("Trigger stage")}
                    placeholder={t("Please select")}
                    isDisabled={!initialValues.is_active}
                    isRequired
                    isLoading={isLoadingTriggerStages}
                    testID="ats-integration-trigger-stage-select"
                  >
                    {triggerStages?.map(({ id, text }) => (
                      <SelectItem key={id} id={id}>
                        {text}
                      </SelectItem>
                    ))}
                  </SelectFormikField>
                </section>
                <SelectFormikField
                  label={t("Default package")}
                  testID="default-package"
                  name="lever_integration.default_package_id"
                  isDisabled={!initialValues.is_active}
                  isRequired
                  isLoading={isLoadingPackages}
                  info={<DefaultPackageWhatsThis />}
                >
                  {packages?.map(({ id, name }) => (
                    <SelectItem key={id} id={id} textValue={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectFormikField>
                <LinkButton
                  onPress={() => window.open("/packages/create/")}
                  testID="lever-create-package-link"
                >
                  {t("Create a new package")}
                </LinkButton>
                <SelectFormikField
                  label={t("Customer group")}
                  testID="customer-group"
                  name="lever_integration.customer_group_id"
                  isDisabled={!initialValues.is_active}
                  isRequired
                  isLoading={isLoadingCustomerGroups}
                >
                  {customerGroups?.map(({ id, name }) => (
                    <SelectItem key={id} id={id} textValue={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectFormikField>
                <LinkButton
                  onPress={() => window.open("/groups/create/")}
                  testID="lever-create-groups-link"
                >
                  {t("Create a new group")}
                </LinkButton>
                <Stack orientation="horizontal" justify="end" gap="space-4">
                  <BasicButton
                    testID="cancel-lever"
                    type="button"
                    isDisabled={isSubmitting}
                    onPress={() => navigate("/settings/ats-integrations")}
                  >
                    {t("Cancel")}
                  </BasicButton>
                  <PrimaryButton
                    testID="save-lever"
                    type="submit"
                    isDisabled={!initialValues.is_active || isSubmitting}
                  >
                    {t("Save changes")}
                  </PrimaryButton>
                </Stack>
              </Stack>
            </Card>
          </Form>
        </Formik>
      </Stack>
    </>
  );
}

const styles = {
  textFieldSpacing: css({ display: "flex", flex: "1" }),
  stack: css({
    gap: "var(--space-4)",
  }),
  card: css({
    marginTop: "var(--space-4)",
    padding: "var(--space-4)",
    gap: "var(--space-4)",
  }),
};
